<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import PrintFileView from "@/components/order/PrintFileView.vue"
import SwatchColorView from "@/components/order/SwatchColorView.vue"
import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InfoRow from "@/components/shared/InfoRow.vue"
import InfoTable from "@/components/shared/InfoTable.vue"
import InputBox from "@/components/shared/InputBox.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import router from "@/plugins/router"
import purchaseProductService from "@/services/purchase-product"
import type { PurchaseProduct } from "@/types/purchase-product.type"
import { reportSentry } from "@/utils/sentry"
import { unitedNumber } from "@/utils/string"

const route = useRoute()
const { t } = useI18n()

const pageTitle = computed(() => {
  return t("shipOut.actions.printShippingMark")
})

const purProductId = computed(() => route.params.pur_product_id)
const purProduct = ref<PurchaseProduct | null>(null)

const cutCount = ref(null)

async function loadProduct() {
  const data = await purchaseProductService.get(Number(purProductId.value))
  purProduct.value = data
}

onMounted(async () => {
  try {
    await loadProduct()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/ship_outs")
  }
})

const isSubmitting = ref(false)

function submitForm() {
  isSubmitting.value = true
  router.push(`/ship_outs/${purProductId.value}/print_mark?cutCount=${cutCount.value}`)
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <form @submit.prevent="submitForm()">
      <div class="row">
        <div class="col-12 col-lg-6">
          <Card v-if="purProduct">
            <CardTitle>{{ t("shipOut.history") }}</CardTitle>
            <CardContent>
              <div class="row">
                <div class="col-6">
                  <InfoTable>
                    <InfoRow :name="t('order.title')">
                      <router-link :to="`/orders/${purProduct.purchase.id}`">
                        {{ purProduct.purchase.code }}
                      </router-link>
                    </InfoRow>
                    <InfoRow :name="t('item.code')">
                      {{ purProduct.product.quality.code }}
                    </InfoRow>
                    <InfoRow :name="t('item.title')">
                      <router-link :to="`/items/${purProduct.product.quality.id}`">
                        {{ purProduct.product.quality.name }}
                      </router-link>
                    </InfoRow>
                    <InfoRow v-if="purProduct.salesType === 'standard_order'" :name="t('colorProduct.colorCode')">
                      {{ purProduct.product.colorCode }}
                    </InfoRow>
                    <InfoRow v-if="purProduct.salesType === 'print'" :name="t('colorProduct.colorCode')">
                      {{ t("orderItem.salesTypeList.print") }}
                    </InfoRow>
                    <InfoRow :name="t('order.quantity')">
                      {{ unitedNumber(purProduct.quantity, t("item.units.yards_short")) }}
                    </InfoRow>
                  </InfoTable>
                </div>
                <div class="col-6">
                  <SwatchColorView
                    v-if="purProduct.salesType === 'standard_order'"
                    :product="purProduct.product"
                    :isForPurchase="false"
                  />
                  <PrintFileView
                    v-if="purProduct.salesType === 'print'"
                    :purProduct="purProduct"
                    :isForPurchase="false"
                  />
                </div>
              </div>
              <InputBox
                type="number"
                v-model="cutCount"
                :label="t('shipOut.cutCount')"
                :guide="t('shipOut.guides.cutCount')"
                field="cutCount"
                :min="1"
                class="mt-3"
                :required="true"
              />
            </CardContent>
            <CardActions justifyEnd>
              <Button type="submit" :text="t('shared.next')" color="warning" :isSubmitting="isSubmitting" />
            </CardActions>
          </Card>
        </div>
      </div>
    </form>
  </LayoutAuthed>
</template>

<style scoped></style>
